import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, FileText, Film, Share2, Wand2, Loader2, Image as ImageIcon } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useLang, BLOG_INFO } from "../../context/LangContext";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const TYPES = [
  { id: "article", icon: FileText, pt: "Artigo", en: "Article" },
  { id: "video", icon: Film, pt: "Vídeo", en: "Video" },
  { id: "social_post", icon: Share2, pt: "Posts Sociais", en: "Social Posts" },
];
const TONES = [["formal", "Formal", "Formal"], ["simples", "Simples", "Simple"], ["tecnico", "Técnico", "Technical"], ["amigavel", "Amigável", "Friendly"]];
const LENGTHS = [["curto", "Curto", "Short"], ["medio", "Médio", "Medium"], ["longo", "Longo", "Long"]];
const VIDEO_FORMATS = [["shorts", "Shorts (9:16)", "Shorts (9:16)"], ["wide", "Horizontal (16:9)", "Wide (16:9)"]];
const VOICES = [["onyx", "Onyx (grave)", "Onyx (deep)"], ["nova", "Nova (enérgica)", "Nova (energetic)"], ["shimmer", "Shimmer (clara)", "Shimmer (bright)"], ["alloy", "Alloy (neutra)", "Alloy (neutral)"], ["echo", "Echo (calma)", "Echo (calm)"]];
const PLATFORMS = ["twitter", "linkedin", "instagram", "facebook", "youtube"];

export default function GenerateTab({ onGenerated }) {
  const { t, lang } = useLang();
  const [blog, setBlog] = useState("suica");
  const [type, setType] = useState("article");
  const [mode, setMode] = useState("preset");
  const [presets, setPresets] = useState({});
  const [presetId, setPresetId] = useState("");
  const [topic, setTopic] = useState("");
  const [prompt, setPrompt] = useState("");
  const [tone, setTone] = useState("simples");
  const [length, setLength] = useState("medio");
  const [style, setStyle] = useState("editorial");
  const [clang, setClang] = useState(lang);

  const [withImages, setWithImages] = useState(true);
  const [withSeo, setWithSeo] = useState(true);
  const [withVideo, setWithVideo] = useState(false);
  const [withSocial, setWithSocial] = useState(false);
  const [videoType, setVideoType] = useState("narrado");
  const [videoFormat, setVideoFormat] = useState("shorts");
  const [voice, setVoice] = useState("onyx");
  const [aiPresets, setAiPresets] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [platforms, setPlatforms] = useState(PLATFORMS);
  const [cats, setCats] = useState({});
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/presets").then((r) => setPresets(r.data.presets || {})).catch(() => {});
    api.get("/public/categories").then((r) => setCats(r.data || {})).catch(() => {});
    api.get("/settings").then((r) => {
      const s = r.data;
      setTone(s.default_tone || "simples"); setLength(s.default_length || "medio");
      setStyle(s.default_style || "editorial");
    }).catch(() => {});
  }, []);

  useEffect(() => { setPresetId(""); setCategory(""); setAiPresets([]); }, [blog]);

  const blogPresets = [...(presets[blog] || []), ...aiPresets];
  const activePreset = blogPresets.find((p) => p.id === presetId);

  const suggestPresets = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.get(`/presets/suggest?blog=${blog}`);
      if ((data.presets || []).length === 0) {
        toast.error(t("Não foi possível sugerir agora. Tenta novamente.", "Couldn't suggest now. Try again."));
      } else {
        setAiPresets(data.presets);
        toast.success(t("Sugestões adicionadas à lista.", "Suggestions added to the list."));
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSuggesting(false); }
  };

  const buildPrompt = () => {
    if (mode === "manual") return prompt.trim();
    if (!activePreset) return "";
    return activePreset.template.replace("{x}", topic.trim() || "…");
  };

  const finalPrompt = buildPrompt();
  const togglePlatform = (p) => setPlatforms((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));

  const generate = async () => {
    if (!finalPrompt || finalPrompt.includes("…")) {
      toast.error(t("Preenche o tema ou o prompt.", "Fill the topic or prompt."));
      return;
    }
    setLoading(true);
    try {
      await api.post("/generate", {
        blog, type, prompt: finalPrompt, tone, length, style, language: clang,
        with_images: withImages, with_seo: withSeo, with_video: withVideo, with_social: withSocial,
        video_type: videoType, video_format: videoFormat, voice, platforms, category: category || null,
      });
      toast.success(t("A gerar em segundo plano… vê o progresso nos Drafts.", "Generating in the background… track progress in Drafts."));
      onGenerated && onGenerated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2 space-y-6">
        {/* Blog + type */}
        <div className="rounded-2xl border border-white/10 bg-card p-6">
          <h3 className="font-heading text-lg font-bold flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" />{t("Gerar Conteúdo com IA", "Generate Content with AI")}</h3>
          <p className="text-sm text-muted-foreground mt-1">{t("Escolhe o blog e o tipo de conteúdo.", "Choose the blog and content type.")}</p>

          <Label className="mt-5 block text-xs uppercase tracking-widest text-muted-foreground">{t("Blog", "Blog")}</Label>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {Object.keys(BLOG_INFO).map((b) => (
              <button key={b} data-testid={`gen-blog-${b}`} onClick={() => setBlog(b)}
                className={`rounded-xl border p-3 text-sm font-semibold transition-colors ${blog === b ? "text-foreground" : "border-white/10 text-muted-foreground hover:text-foreground"}`}
                style={blog === b ? { borderColor: BLOG_INFO[b].color, backgroundColor: `${BLOG_INFO[b].color}1a` } : {}}>
                <span className="inline-block h-2 w-2 rounded-full mr-2" style={{ backgroundColor: BLOG_INFO[b].color }} />
                {lang === "pt" ? BLOG_INFO[b].pt : BLOG_INFO[b].en}
              </button>
            ))}
          </div>

          <Label className="mt-5 block text-xs uppercase tracking-widest text-muted-foreground">{t("Tipo", "Type")}</Label>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {TYPES.map((ty) => (
              <button key={ty.id} data-testid={`gen-type-${ty.id}`} onClick={() => setType(ty.id)}
                className={`rounded-xl border p-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${type === ty.id ? "border-primary bg-primary/10 text-foreground" : "border-white/10 text-muted-foreground hover:text-foreground"}`}>
                <ty.icon className="h-4 w-4" />{lang === "pt" ? ty.pt : ty.en}
              </button>
            ))}
          </div>
        </div>

        {/* Prompt */}
        <div className="rounded-2xl border border-white/10 bg-card p-6">
          <div className="flex gap-2 mb-4">
            <button data-testid="mode-preset-btn" onClick={() => setMode("preset")} className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${mode === "preset" ? "bg-primary text-primary-foreground" : "bg-white/5 text-muted-foreground"}`}>{t("Prompt pré-definido", "Preset prompt")}</button>
            <button data-testid="mode-manual-btn" onClick={() => setMode("manual")} className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${mode === "manual" ? "bg-primary text-primary-foreground" : "bg-white/5 text-muted-foreground"}`}>{t("Prompt manual", "Manual prompt")}</button>
          </div>

          {mode === "preset" ? (
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between">
                  <Label>{t("Modelo de prompt", "Prompt template")}</Label>
                  <Button data-testid="suggest-presets-btn" size="sm" variant="secondary" className="h-7 gap-1.5 text-xs" onClick={suggestPresets} disabled={suggesting}>
                    {suggesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}{t("Sugerir com IA", "Suggest with AI")}
                  </Button>
                </div>
                <Select value={presetId} onValueChange={setPresetId}>
                  <SelectTrigger data-testid="preset-select" className="mt-1.5"><SelectValue placeholder={t("Escolher…", "Choose…")} /></SelectTrigger>
                  <SelectContent>
                    {blogPresets.map((p) => (<SelectItem key={p.id} value={p.id}>{p.ai ? "✨ " : ""}{lang === "pt" ? p.label_pt : p.label_en}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              {activePreset && (
                <div>
                  <Label>{t("Tema / detalhe", "Topic / detail")}</Label>
                  <Input data-testid="preset-topic-input" value={topic} onChange={(e) => setTopic(e.target.value)} className="mt-1.5" placeholder={t("ex.: seguro de saúde", "e.g. health insurance")} />
                </div>
              )}
            </div>
          ) : (
            <div>
              <Label>{t("O teu prompt", "Your prompt")}</Label>
              <Textarea data-testid="manual-prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} className="mt-1.5" placeholder={t("Gerar artigo sobre: Como funciona o seguro de saúde na Suíça", "Write an article about: How Swiss health insurance works")} />
            </div>
          )}

          {finalPrompt && !finalPrompt.includes("…") && (
            <div className="mt-3 rounded-lg border border-white/10 bg-background/50 p-3 text-sm text-slate-300"><span className="text-muted-foreground">{t("Prompt final:", "Final prompt:")}</span> {finalPrompt}</div>
          )}
        </div>
      </div>

      {/* Right: options */}
      <div className="space-y-6">
        <div className="rounded-2xl border border-white/10 bg-card p-6 space-y-4">
          <h3 className="font-heading font-bold">{t("Configurações", "Settings")}</h3>
          <div>
            <Label className="text-xs">{t("Idioma do conteúdo", "Content language")}</Label>
            <Select value={clang} onValueChange={setClang}><SelectTrigger data-testid="gen-lang-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="pt">Português</SelectItem><SelectItem value="en">English</SelectItem></SelectContent></Select>
          </div>
          {type === "article" && (cats[blog] || []).length > 0 && (
            <div>
              <Label className="text-xs">{t("Categoria", "Category")}</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="gen-category-select" className="mt-1.5"><SelectValue placeholder={t("Sem categoria", "No category")} /></SelectTrigger>
                <SelectContent>
                  {(cats[blog] || []).map((c) => <SelectItem key={c.id} value={c.id}>{lang === "pt" ? c.pt : c.en}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">{t("Tom", "Tone")}</Label>
            <Select value={tone} onValueChange={setTone}><SelectTrigger data-testid="gen-tone-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{TONES.map(([v, pt, en]) => <SelectItem key={v} value={v}>{lang === "pt" ? pt : en}</SelectItem>)}</SelectContent></Select>
          </div>
          {type === "article" && (
            <>
              <div>
                <Label className="text-xs">{t("Tamanho", "Length")}</Label>
                <Select value={length} onValueChange={setLength}><SelectTrigger data-testid="gen-length-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{LENGTHS.map(([v, pt, en]) => <SelectItem key={v} value={v}>{lang === "pt" ? pt : en}</SelectItem>)}</SelectContent></Select>
              </div>
              <div>
                <Label className="text-xs">{t("Estilo de escrita", "Writing style")}</Label>
                <Input data-testid="gen-style-input" value={style} onChange={(e) => setStyle(e.target.value)} className="mt-1.5" />
              </div>
            </>
          )}
          {type === "video" && (
            <>
              <div>
                <Label className="text-xs">{t("Formato do vídeo", "Video format")}</Label>
                <Select value={videoFormat} onValueChange={setVideoFormat}><SelectTrigger data-testid="gen-videoformat-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{VIDEO_FORMATS.map(([v, pt, en]) => <SelectItem key={v} value={v}>{lang === "pt" ? pt : en}</SelectItem>)}</SelectContent></Select>
              </div>
              <div>
                <Label className="text-xs">{t("Voz da narração", "Narration voice")}</Label>
                <Select value={voice} onValueChange={setVoice}><SelectTrigger data-testid="gen-voice-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{VOICES.map(([v, pt, en]) => <SelectItem key={v} value={v}>{lang === "pt" ? pt : en}</SelectItem>)}</SelectContent></Select>
              </div>
              <p className="text-xs text-muted-foreground">{t("Gera um vídeo MP4 real: narração, imagens e legendas. Demora 1-2 min em segundo plano.", "Builds a real MP4 video: narration, images and captions. Takes 1-2 min in the background.")}</p>
            </>
          )}
        </div>

        <div className="rounded-2xl border border-white/10 bg-card p-6 space-y-3">
          <h3 className="font-heading font-bold">{t("Extras", "Extras")}</h3>
          {(type === "article" || type === "video") && (
            <label className="flex items-center justify-between text-sm"><span className="flex items-center gap-2"><ImageIcon className="h-4 w-4 text-muted-foreground" />{t("Gerar imagem IA", "Generate AI image")}</span><Switch data-testid="opt-images" checked={withImages} onCheckedChange={setWithImages} /></label>
          )}
          {type === "article" && <>
            <label className="flex items-center justify-between text-sm"><span>SEO</span><Switch data-testid="opt-seo" checked={withSeo} onCheckedChange={setWithSeo} /></label>
            <label className="flex items-center justify-between text-sm"><span>{t("Incluir guião de vídeo", "Include video script")}</span><Switch data-testid="opt-video" checked={withVideo} onCheckedChange={setWithVideo} /></label>
            <label className="flex items-center justify-between text-sm"><span>{t("Incluir posts sociais", "Include social posts")}</span><Switch data-testid="opt-social" checked={withSocial} onCheckedChange={setWithSocial} /></label>
          </>}
          {(type === "social_post" || (type === "article" && withSocial)) && (
            <div>
              <Label className="text-xs">{t("Plataformas", "Platforms")}</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {PLATFORMS.map((p) => (
                  <button key={p} data-testid={`platform-${p}`} onClick={() => togglePlatform(p)} className={`rounded-full px-3 py-1 text-xs font-semibold capitalize border transition-colors ${platforms.includes(p) ? "border-primary bg-primary/10 text-foreground" : "border-white/10 text-muted-foreground"}`}>{p}</button>
                ))}
              </div>
            </div>
          )}
        </div>

        <Button data-testid="generate-btn" onClick={generate} disabled={loading} className="w-full gap-2 h-12 text-base">
          {loading ? <><Loader2 className="h-5 w-5 animate-spin" />{t("A gerar… (pode demorar)", "Generating… (may take a while)")}</> : <><Wand2 className="h-5 w-5" />{t("Gerar com IA", "Generate with AI")}</>}
        </Button>
      </div>
    </div>
  );
}
